import { Component, computed, input, InputSignal, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { KYC_KYB_URLS } from '@app/models/kyc-kyb/types';
import { PersonaScope } from '@app/modules/payroll/components/kyc-kyb/types';
import { AppDatePipe } from '@app/pipes';
import { TranslateModule } from '@ngx-translate/core';
import { BaseInformationBar } from '../base-information-type.component';

/**
 * Banner shown to users that must complete the Know-Your-Client or Know-Your-Business flow through Persona
 */
@Component({
    selector: 'special-info-kyc-kyb-with-wave',
    standalone: true,
    imports: [TranslateModule, MatIconModule, RouterLink, AppDatePipe],
    templateUrl: './kyc-kyb-with-wave.template.html',
    styleUrls: ['./kyc-kyb-with-wave.style.scss'],
})
export class SpecialInfoKYCKYBWithWave extends BaseInformationBar {
    readonly WAVE_1_DEADLINE = '2025-07-06T04:00:00.000Z'; // 12am Jul 6, 2025 EDT
    readonly WAVE_2_DEADLINE = '2025-10-01T04:00:00.000Z'; // 12am Oct 1, 2025 EDT
    readonly WAVE_3_DEADLINE = '2026-02-03T05:00:00.000Z'; // 12am Feb 3, 2026 EDT

    scope: InputSignal<PersonaScope> = input.required();

    KYBKYCExpirationDate: Signal<string> = computed(() => {
        const { KYCKYBWaveOne, KYCKYBWaveTwo, KYCKYBWaveThree } = this.displayConditions() || {};

        if (KYCKYBWaveOne) {
            return this.WAVE_1_DEADLINE;
        }
        if (KYCKYBWaveTwo) {
            return this.WAVE_2_DEADLINE;
        }
        if (KYCKYBWaveThree) {
            return this.WAVE_3_DEADLINE;
        }
        return '';
    });

    shouldDisplay: Signal<boolean> = computed(() => {
        const {
            hasPayroll,
            isAdmin,
            KYCKYB,
            currentURL,
            KYCKYBWaveOne,
            KYCKYBWaveTwo,
            KYCKYBWaveThree,
            hasKYCExpired,
            isEmployeeOnboarding,
            hasKYBExpired,
        } = this.displayConditions() || {};

        // Don't show if employee is onboarding
        if (isEmployeeOnboarding) {
            return false;
        }

        // Don't show if re-verification is required (re-verification banner takes priority)
        if (hasKYBExpired) {
            return false;
        }

        // Do not show if KYC/KYB is disabled, or this company is not part of any waves
        if (!KYCKYB || (!KYCKYBWaveOne && !KYCKYBWaveTwo && !KYCKYBWaveThree)) {
            return false;
        }

        const { hasVerifiedPersonaIdentity, hasVerifiedPersonaBusiness } = KYCKYB;

        // If we're already on the KYC or KYB page we don't need to direct to itself (KYC still links to KYB and vice-versa)
        if (KYC_KYB_URLS[this.scope()] === currentURL) {
            return false;
        }

        // KYC
        if (this.scope() === 'KYC') {
            if ((hasPayroll || hasKYCExpired) && !hasVerifiedPersonaIdentity) {
                return true;
            }

            return false;
        }

        // KYB
        if (hasPayroll && !!isAdmin && !hasVerifiedPersonaBusiness) {
            return true;
        }
        return false;
    });

    get linkToPreRedirect(): string {
        return KYC_KYB_URLS[this.scope()];
    }

    get hasKYCExpired(): boolean {
        return this.displayConditions()?.hasKYCExpired ?? false;
    }

    get verificationDays(): number {
        const displayConditions = this.displayConditions();
        return displayConditions?.KYCKYBWaveThree ? 90 : 60;
    }
}
